import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  getLicense, getHistory, getInstancesByLicense,
  disableInstance, enableInstance,
  getPackagesForLicense, requestUpdate, getInstanceUpdateHistory, updateLicenseCode, updateLicenseName
} from '../../services/api';
import { pill } from '../pill';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Pager from '../ui/Pager';
import Highlight from '../ui/Highlight';
import CopyButton from '../ui/CopyButton';
import LicenseCodeInfo from './LicenseCodeInfo';

const STATUS_COLORS = {
  REQUESTED: { bg: '#fff4e0', color: '#955d00', label: '⏳ Update requested' },
  COMPLETED: { bg: '#e2f3e8', color: '#1a7f37', label: '✓ Updated' },
  FAILED:    { bg: '#fdecea', color: '#c62828', label: '✗ Failed' },
  PENDING:   { bg: '#ffffff', color: '#6b7280', label: '○ Pending' },
};

/** Compare two semver strings. Returns -1, 0 or 1. */
function compareSemver(a, b) {
  if (!a) return -1;
  if (!b) return 1;
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const na = pa[i] || 0, nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

export default function LicenseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [history, setHistory] = useState([]);
  const [instances, setInstances] = useState([]);
  const [days, setDays] = useState(30);
  const [showDisabled, setShowDisabled] = useState(false);  // DB-4497

  // Modals
  const [disableModal, setDisableModal] = useState(null);
  const [reason, setReason] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [historyModal, setHistoryModal] = useState(null);
  const [historyResult, setHistoryResult] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [historyPage, setHistoryPage] = useState(0);

  // Package panel: { instance, pendingUpdate } — liste paginée côté API (~100k packages/an)
  const [packagePanel, setPackagePanel] = useState(null);
  const [pkgResult, setPkgResult] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [pkgPage, setPkgPage] = useState(0);
  const [pkgQuery, setPkgQuery] = useState('');

  const load = () => {
    getLicense(id).then(setLicense);
    getHistory(id, days).then(pts => {
      const map = {};
      pts.forEach(p => {
        const key = p.timestamp;
        if (!map[key]) map[key] = { timestamp: p.timestamp, total: 0 };
        map[key].total += p.uniqueUsers;
        map[key][p.serverName] = p.uniqueUsers;
      });
      setHistory(Object.values(map).sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)));
    });
    getInstancesByLicense(id).then(setInstances);
  };

  useEffect(() => { load(); }, [id, days]); // eslint-disable-line

  // Panneau packages : page courante, recherche appliquée après 300 ms
  useEffect(() => {
    if (!packagePanel) return undefined;
    const t = setTimeout(() => {
      getPackagesForLicense(id, { q: pkgQuery || undefined, page: pkgPage, size: 20, sort: 'uploadedAt', dir: 'desc' })
        .then(setPkgResult);
    }, pkgQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [id, packagePanel, pkgPage, pkgQuery]);

  useEffect(() => {
    if (!historyModal) return;
    getInstanceUpdateHistory(historyModal.instanceId, { page: historyPage, size: 20 }).then(setHistoryResult);
  }, [historyModal, historyPage]);

  const handleDisable = async () => {
    await disableInstance(disableModal, reason);
    setDisableModal(null); setReason(''); load();
  };

  const handleEnable = async instanceId => {
    await enableInstance(instanceId); load();
  };

  const handleRequestUpdate = async (instanceId, packageId) => {
    await requestUpdate(instanceId, packageId, updateNotes);
    setUpdateNotes('');
    setPackagePanel(null);
    load();
  };

  const openHistory = inst => {
    setHistoryResult({ content: [], totalPages: 0, totalElements: 0 });
    setHistoryPage(0);
    setHistoryModal({ instanceId: inst.id, name: inst.serverName });
  };

  const openPackagePanel = inst => {
    setPkgResult({ content: [], totalPages: 0, totalElements: 0 });
    setPkgPage(0);
    setPkgQuery('');
    setPackagePanel(inst);
    setUpdateNotes('');
  };
  const packages = pkgResult.content;
  const updateHistory = historyResult.content;

  if (!license) return <p style={{ color: '#6b7280' }}>Loading...</p>;

  const serverNames = [...new Set(history.flatMap(h =>
    Object.keys(h).filter(k => k !== 'timestamp' && k !== 'total')))];
  const COLORS = ['#005cc5', '#1a7f37', '#b45309', '#5f37a8', '#c62828'];

  // DB-4497 : les instances désactivées (active=false) sont séparées ; masquées par
  // défaut, réaffichables via l'interrupteur, et exclues des compteurs.
  const activeInstances   = instances.filter(i => i.active !== false);
  const disabledInstances = instances.filter(i => i.active === false);
  const visibleInstances  = showDisabled
    ? [...activeInstances, ...disabledInstances]
    : activeInstances;

  return (
    <div>
      <Button onClick={() => navigate(-1)} style={{ marginBottom: '1.5rem' }}>← Back</Button>

      {/* Header KPIs */}
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <LicenseName license={license} onChange={setLicense} />
          <LicenseIdentity license={license} onChange={setLicense} />
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <KpiBox label="Unique users" value={license.currentUniqueUsers}
                  color={license.compliant ? '#1a7f37' : '#c62828'} />
          <KpiBox label="Quota" value={license.maxUsers} color="#005cc5" />
          <KpiBox label="Window" value={`${license.uniqueUserWindowDays}d`} color="#b45309" />
          <KpiBox label="Instances"
                  value={instances.length ? activeInstances.length : license.instanceCount}
                  color="#5f37a8" />
        </div>
      </div>

      {/* Chart */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontWeight: 600 }}>Unique users over time</span>
          <select value={days} onChange={e => setDays(+e.target.value)} style={selectStyle}>
            {[7, 14, 30, 90].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={history}>
            <CartesianGrid strokeDasharray="3 3" stroke="#cdcdcd" />
            <XAxis dataKey="timestamp" stroke="#8a8f99"
                   tickFormatter={v => new Date(v).toLocaleDateString('fr-FR')}
                   tick={{ fontSize: 11 }} />
            <YAxis stroke="#8a8f99" tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #cdcdcd', color: '#444444' }}
                     labelFormatter={v => new Date(v).toLocaleString('fr-FR')} />
            <Legend />
            <ReferenceLine y={license.maxUsers} stroke="#c62828" strokeDasharray="5 5"
                           label={{ value: 'Quota', fill: '#c62828', fontSize: 11 }} />
            {serverNames.map((name, i) => (
              <Line key={name} type="monotone" dataKey={name}
                    stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Instances table */}
      <div style={{ ...card, marginTop: '1.5rem', overflowX: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontWeight: 600 }}>
            Server instances ({activeInstances.length})
          </span>
          {disabledInstances.length > 0 && (
            <label style={{ fontSize: '0.82rem', color: '#6b7280', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="checkbox" checked={showDisabled}
                     onChange={e => setShowDisabled(e.target.checked)} />
              Afficher les {disabledInstances.length} désactivée{disabledInstances.length > 1 ? 's' : ''}
            </label>
          )}
        </div>
        <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase',
                         borderBottom: '1px solid #cdcdcd' }}>
              {['Server', 'IP', 'Version', 'Users', 'Last seen', 'Update status', 'Actions'].map(h =>
                <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleInstances.map(inst => {
              const upd = inst.latestUpdateStatus
                ? STATUS_COLORS[inst.latestUpdateStatus] : null;
              const isDisabled = inst.active === false;   // DB-4497
              return (
                <tr key={inst.id} style={{ borderBottom: '1px solid #ececec',
                                           ...(isDisabled ? { opacity: 0.5, background: '#fafafa' } : {}) }}>
                  <td style={td}>
                    {inst.serverName}
                    {isDisabled && (
                      <span title={inst.disableReason || 'Instance désactivée'} style={disabledBadge}>
                        Désactivée{inst.disabledAt
                          ? ` · ${new Date(inst.disabledAt).toLocaleDateString('fr-FR')}` : ''}
                      </span>
                    )}
                    {inst.approvalStatus === 'PENDING' && (
                      <span title="Aucun package tant que l'instance n'est pas approuvée (page Instances)"
                            style={pendingBadge}>
                        À approuver
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#6b7280', fontSize: '0.82rem' }}>
                    {inst.serverIp}
                  </td>
                  <td style={td}>
                    {inst.currentVersion
                      ? <span style={{ fontFamily: 'monospace', color: '#005cc5' }}>
                          v{inst.currentVersion}
                        </span>
                      : <span style={{ color: '#8a8f99' }}>—</span>}
                  </td>
                  <td style={td}>{inst.lastReportedUsers ?? '—'}</td>
                  <td style={{ ...td, fontSize: '0.82rem', color: '#6b7280' }}>
                    {inst.lastSeenAt
                      ? new Date(inst.lastSeenAt).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td style={td}>
                    {upd
                      ? <span style={pill(upd.bg, upd.color)}>
                          {upd.label}
                          {inst.pendingUpdateVersion &&
                            ` → v${inst.pendingUpdateVersion}`}
                          {inst.latestUpdateStatus === 'COMPLETED' && inst.lastUpdateCompletedAt &&
                            <span style={{ fontWeight: 400, marginLeft: '4px' }}>
                              ({new Date(inst.lastUpdateCompletedAt).toLocaleDateString('fr-FR')})
                            </span>}
                        </span>
                      : <span style={{ color: '#8a8f99', fontSize: '0.82rem' }}>—</span>}
                  </td>
                  <td style={{ ...td, display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {!isDisabled && (
                      <Button size="sm" onClick={() => openPackagePanel(inst)}>📦 Packages</Button>
                    )}
                    <Button size="sm" onClick={() => openHistory(inst)}>History</Button>
                    {isDisabled
                      ? <Button size="sm" variant="success" onClick={() => handleEnable(inst.id)}>Enable</Button>
                      : <Button size="sm" variant="danger" onClick={() => setDisableModal(inst.id)}>Disable</Button>}
                  </td>
                </tr>
              );
            })}
            {visibleInstances.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '1.5rem' }}>
                  {disabledInstances.length > 0
                    ? 'Aucune instance active — coche « Afficher les désactivées » pour les voir.'
                    : 'Aucune instance rattachée à cette licence.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Package panel modal ───────────────────────────────────────── */}
      {packagePanel && (
        <Modal title={`Available packages — ${packagePanel.serverName}`} width={750}
               onClose={() => setPackagePanel(null)}
               subtitle={
                <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                  Current version:&nbsp;
                  <span style={{ color: '#005cc5', fontFamily: 'monospace' }}>
                    {packagePanel.currentVersion
                      ? `v${packagePanel.currentVersion}` : 'unknown'}
                  </span>
                  {packagePanel.latestUpdateStatus === 'REQUESTED' && (
                    <span style={pill('#fff4e0', '#955d00',
                                      { display: 'inline-block', marginLeft: '1rem', fontWeight: 400 })}>
                      ⏳ Update in progress — buttons locked
                    </span>
                  )}
                </span>}>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Rechercher un package (nom, version, fichier, notes)</label>
              <input style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}
                     placeholder="SNCFIMMO-422, 1.1.0…" value={pkgQuery}
                     onChange={e => { setPkgQuery(e.target.value); setPkgPage(0); }} />
            </div>

            {/* Optional notes field */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Notes for the update request (optional)</label>
              <input style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}
                     placeholder="e.g. scheduled maintenance window..."
                     value={updateNotes}
                     onChange={e => setUpdateNotes(e.target.value)} />
            </div>

            <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#8a8f99', fontSize: '0.78rem',
                             textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
                  {['Version', 'File', 'Target', 'Release Notes', 'Uploaded', 'Action'].map(h =>
                    <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {packages.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '2rem' }}>
                      No packages available for this license
                    </td>
                  </tr>
                )}
                {packages.map(pkg => {
                  const cmp = compareSemver(pkg.version, packagePanel.currentVersion);
                  // cmp > 0 : pkg newer  |  cmp = 0 : same  |  cmp < 0 : older
                  const isOlder    = cmp < 0;
                  const isCurrent  = cmp === 0;
                  const isNewer    = cmp > 0;
                  const canRequest = (isCurrent || isNewer)
                    && packagePanel.latestUpdateStatus !== 'REQUESTED';

                  return (
                    <tr key={pkg.id}
                        style={{
                          borderBottom: '1px solid #ececec',
                          opacity: isOlder ? 0.45 : 1,
                          background: isNewer ? 'rgba(35,40,83,0.08)' : '',
                        }}>
                      <td style={td}>
                        <span style={{
                          fontFamily: 'monospace', fontSize: '0.85rem',
                          color: isNewer ? '#005cc5' : isCurrent ? '#1a7f37' : '#8a8f99',
                          fontWeight: isNewer || isCurrent ? 700 : 400,
                        }}>
                          v<Highlight text={pkg.version} query={pkgQuery} />
                        </span>
                        {isCurrent && (
                          <span style={{ marginLeft: '6px', background: '#e2f3e8',
                                         color: '#1a7f37', padding: '1px 6px',
                                         borderRadius: '4px', fontSize: '0.7rem' }}>
                            current
                          </span>
                        )}
                        {isNewer && (
                          <span style={{ marginLeft: '6px', background: '#232853',
                                         color: '#005cc5', padding: '1px 6px',
                                         borderRadius: '4px', fontSize: '0.7rem' }}>
                            newer
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace',
                                   fontSize: '0.78rem', color: '#6b7280' }}>
                        <Highlight text={pkg.fileName} query={pkgQuery} />
                      </td>
                      <td style={td}>
                        {pkg.clientName
                          ? <span style={{ color: '#5f37a8', fontSize: '0.82rem' }}>
                              👤 {pkg.clientName}
                            </span>
                          : <span style={{ color: '#1a7f37', fontSize: '0.82rem' }}>
                              🌍 Global
                            </span>}
                      </td>
                      <td style={{ ...td, maxWidth: '200px' }}>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem',
                                       display: 'block', overflow: 'hidden',
                                       textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={pkg.releaseNotes}>
                          {pkg.releaseNotes ? <Highlight text={pkg.releaseNotes} query={pkgQuery} radius={12} /> : '—'}
                        </span>
                      </td>
                      <td style={{ ...td, fontSize: '0.78rem', color: '#8a8f99' }}>
                        {new Date(pkg.uploadedAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td style={td}>
                        {canRequest ? (
                          <Button size="sm" variant={isNewer ? 'primary' : 'secondary'}
                                  onClick={() => handleRequestUpdate(packagePanel.id, pkg.id)}>
                            {isNewer ? '⬆ Request' : '↺ Re-deploy'}
                          </Button>
                        ) : isOlder ? (
                          <span style={{ color: '#e6e6e6', fontSize: '0.78rem' }}>
                            older
                          </span>
                        ) : (
                          <span style={{ color: '#8a8f99', fontSize: '0.78rem' }}>
                            🔒 locked
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager page={pkgPage} totalPages={pkgResult.totalPages} totalElements={pkgResult.totalElements}
                   onPage={setPkgPage} />
        </Modal>
      )}

      {/* ── Disable modal ─────────────────────────────────────────────── */}
      {disableModal && (
        <Modal title="Disable instance" width={460} onClose={() => setDisableModal(null)}
               footer={<>
                 <Button onClick={() => setDisableModal(null)}>Cancel</Button>
                 <Button variant="danger" onClick={handleDisable}>Confirm</Button>
               </>}>
            <textarea
              style={{ ...selectStyle, width: '100%', height: '80px',
                       resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Reason for disabling..."
              value={reason} onChange={e => setReason(e.target.value)} />
        </Modal>
      )}

      {/* ── Update history modal ──────────────────────────────────────── */}
      {historyModal && (
        <Modal title={`Update history — ${historyModal.name}`} width={750} onClose={() => setHistoryModal(null)}>
            {updateHistory.length === 0
              ? <p style={{ color: '#8a8f99', textAlign: 'center' }}>No update history</p>
              : (
                <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: '#8a8f99', fontSize: '0.78rem',
                                 textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
                      {['Package', 'From', 'To', 'Status', 'Requested', 'Completed', 'Notes'].map(h =>
                        <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {updateHistory.map(u => {
                      const s = STATUS_COLORS[u.status] || STATUS_COLORS.PENDING;
                      return (
                        <tr key={u.id} style={{ borderBottom: '1px solid #ececec' }}>
                          <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.82rem' }}>
                            {u.packageName || '—'}
                            <span style={{ color: '#8a8f99', fontSize: '0.72rem' }}> v{u.packageVersion}</span>
                          </td>
                          <td style={{ ...td, color: '#6b7280', fontSize: '0.82rem' }}>
                            {u.fromVersion || '—'}
                          </td>
                          <td style={{ ...td, color: '#005cc5', fontSize: '0.82rem' }}>
                            {u.toVersion}
                          </td>
                          <td style={td}>
                            <span style={pill(s.bg, s.color, { fontSize: '0.75rem', fontWeight: 400 })}>
                              {u.status}
                            </span>
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', color: '#6b7280' }}>
                            {u.requestedAt
                              ? new Date(u.requestedAt).toLocaleString('fr-FR') : '—'}<br/>
                            <span style={{ fontSize: '0.72rem' }}>{u.requestedBy}</span>
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', color: '#6b7280' }}>
                            {u.completedAt
                              ? new Date(u.completedAt).toLocaleString('fr-FR') : '—'}
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', color: '#6b7280',
                                       maxWidth: '150px' }}>
                            <span title={u.notes}>{u.notes || '—'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            <Pager page={historyPage} totalPages={historyResult.totalPages}
                   totalElements={historyResult.totalElements} onPage={setHistoryPage} />
        </Modal>
      )}
    </div>
  );
}

/** Nom du client, modifiable sur place (affichage seulement : la livraison repose sur la clé et le code). */
function LicenseName({ license, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    try {
      onChange({ ...license, ...(await updateLicenseName(license.id, draft)) });
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Modification impossible.');
    }
  };

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.5rem' }}>
        <h1 style={{ margin: 0 }}>{license.clientName}</h1>
        <Button size="sm" onClick={() => { setDraft(license.clientName); setError(''); setEditing(true); }}
                title="Renommer le client" style={{ padding: '0.15rem 0.5rem' }}>✎</Button>
      </div>
    );
  }
  return (
    <div style={{ margin: '0 0 0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
        <input value={draft} autoFocus maxLength={255} onChange={e => setDraft(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
               style={{ ...selectStyle, fontSize: '1.4rem', fontWeight: 700, width: '22rem', maxWidth: '100%' }} />
        <Button size="sm" variant="primary" onClick={save}>Enregistrer</Button>
        <Button size="sm" onClick={() => setEditing(false)}>Annuler</Button>
      </div>
      {error && <div style={{ color: '#c62828', fontSize: '0.8rem', marginTop: '0.25rem' }}>{error}</div>}
    </div>
  );
}

/**
 * Identifiants de la licence, chacun copiable : le CODE (à utiliser dans la CI, l'agent de
 * push, les scripts et l'API), l'ID numérique (accepté aussi par l'API) et la CLÉ (à mettre
 * dans PACKAGE_SERVER_TOKEN des instances). Le code est modifiable, avec avertissement.
 */
function LicenseIdentity({ license, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    try {
      onChange({ ...license, ...(await updateLicenseCode(license.id, draft)) });
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Modification impossible.');
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.35rem 0.75rem', alignItems: 'center', fontSize: '0.85rem' }}>
      <span style={{ ...idLabel, display: 'inline-flex', alignItems: 'center' }}>Code<LicenseCodeInfo /></span>
      {editing ? (
        <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={draft} autoFocus onChange={e => setDraft(e.target.value.toUpperCase())}
                 onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
                 style={{ ...selectStyle, fontFamily: 'monospace', width: '12rem' }} />
          <Button size="sm" variant="primary" onClick={save}>Enregistrer</Button>
          <Button size="sm" onClick={() => setEditing(false)}>Annuler</Button>
          <span style={{ color: '#955d00', fontSize: '0.78rem', flexBasis: '100%' }}>
            ⚠ Les jobs CI et scripts qui utilisent « {license.code} » devront être mis à jour.
          </span>
          {error && <span style={{ color: '#c62828', fontSize: '0.8rem', flexBasis: '100%' }}>{error}</span>}
        </span>
      ) : (
        <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <code style={idValue}>{license.code}</code>
          <CopyButton value={license.code} />
          <Button size="sm" onClick={() => { setDraft(license.code); setError(''); setEditing(true); }}
                  style={{ padding: '0.15rem 0.5rem' }}>✎ Modifier</Button>
        </span>
      )}
      <span style={idLabel}>ID</span>
      <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <code style={idValue}>{license.id}</code>
        <CopyButton value={license.id} />
      </span>
      <span style={idLabel}>Clé</span>
      <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <code style={{ ...idValue, fontWeight: 400, color: '#6b7280' }}>{license.licenseKey}</code>
        <CopyButton value={license.licenseKey} title="Copier la clé (PACKAGE_SERVER_TOKEN des instances)" />
      </span>
    </div>
  );
}

function KpiBox({ label, value, color }) {
  return (
    <div style={{ background: '#f5f5f5', padding: '1rem 1.5rem', borderRadius: '10px',
                  textAlign: 'center', minWidth: '110px' }}>
      <div style={{ fontSize: '1.8rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>{label}</div>
    </div>
  );
}

const card        = { background: '#ffffff', borderRadius: '12px', padding: '1.5rem' };
const idLabel     = { color: '#8a8f99', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.03em' };
const idValue     = { color: '#232853', fontWeight: 700, fontSize: '0.9rem' };
const td          = { padding: '0.75rem', color: '#444444' };
const labelStyle  = { display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '4px' };
const selectStyle = { background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444',
                      padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem' };
// DB-4497 : badge des instances désactivées dans la liste « Server instances ».
// Au fil du nom du serveur : inline-block (pas block, qui le renverrait à la ligne)
const disabledBadge = pill('#f0f0f0', '#8a8f99', { display: 'inline-block', marginLeft: '8px',
                                                   border: '1px solid #dcdcdc', fontSize: '0.7rem' });
// DO-207 : nouvelle instance, en attente d'approbation
const pendingBadge = pill('#fff4e0', '#955d00', { display: 'inline-block', marginLeft: '8px',
                                                  border: '1px solid #e0b25e', fontSize: '0.7rem' });
